import * as React from 'react'
import * as Redux from 'react-redux'
import { Link as RouterLink } from 'react-router-dom'
import { styled, css } from 'next-yak'

import * as plume from '@ulule/owl-kit-components/next'
import { t } from '@owl-nest/localize'
import * as model from '@owl-nest/models'
import { CATEGORY, EVENT_TYPE, useTracking } from '@owl-nest/shadow'
import type { YakComponentOf, PolymorphicComponent } from '@owl-nest/yak/extra-runtime'

import { useNavigation } from '../../../hooks/useNavigation'
import { PATHS } from '../../../paths'

import * as modalAction from '../../../../actions/modal'
import * as projectModel from '../../../models/project'
import { type RootState } from '../../../../reducers'
import * as URLs from '../../../utils/URLs'
import { TRACKING_ACTION, TRACKING_SOURCE } from '../utils/trackerEnums'

type MenuProps = {
  className?: string
  sticky: boolean
}

type TooltipValues = 'news' | 'backers' | 'comments' | undefined

function MenuComponent({ className, sticky }: MenuProps): React.ReactElement<MenuProps> {
  const navigation = useNavigation()
  const dispatch = Redux.useDispatch()

  const tracking = useTracking()

  const project = Redux.useSelector<RootState, projectModel.Project>((state) => state.application.project.data)
  const isProjectTab = navigation.is(PATHS.PROJECT)
  const projectIsDonationBased = model.project.isDonationBased(project)

  const [tooltipOpen, setTooltipOpen] = React.useState<TooltipValues>() // 'news'|'backers'|'comments'|undefined

  function toggleTooltip(value: TooltipValues): void {
    if (value !== tooltipOpen) {
      setTooltipOpen(value)
    } else {
      setTooltipOpen(undefined)
    }
  }

  return (
    <>
      <MenuPositionner className={className}>
        <MenuContainer>
          <MenuNav>
            <plume.TabMenu>
              <plume.Tab active={navigation.is(PATHS.PROJECT)}>
                <TabItem as={RouterLink} to={navigation.pathTo(PATHS.PROJECT)}>
                  {t('Project')}
                </TabItem>
              </plume.Tab>

              {!projectIsDonationBased && (
                <plume.Tab active={navigation.is(PATHS.REWARDS)}>
                  <TabItem as={RouterLink} to={navigation.pathTo(PATHS.REWARDS)}>
                    {t('Rewards')}
                  </TabItem>
                </plume.Tab>
              )}

              <plume.Tab active={navigation.is(PATHS.FAQ)}>
                <TabItem as={RouterLink} to={navigation.pathTo(PATHS.FAQ)}>
                  {t('FAQ')}
                </TabItem>
              </plume.Tab>

              <plume.Tab active={navigation.is(PATHS.NEWS)} disabled={!project.is_online}>
                {project.is_online ? (
                  <TabItem as={RouterLink} to={navigation.pathTo(PATHS.NEWS)}>
                    {t('Posts')}&nbsp;
                    {!!project.news_count && <Counter>{project.news_count}</Counter>}
                  </TabItem>
                ) : (
                  <>
                    <TabItem
                      as={plume.Link}
                      onMouseEnter={() => {
                        toggleTooltip('news')
                      }}
                      onMouseLeave={() => {
                        toggleTooltip(undefined)
                      }}
                      onTouchStart={() => {
                        toggleTooltip('news')
                      }}
                      disabled
                    >
                      {t('Posts')}&nbsp;
                    </TabItem>
                    <plume.Tooltip open={tooltipOpen === 'news'} position={'right'}>
                      <plume.styles.copy.S.regular>
                        {t('This tab will be visible once the project is live')}
                      </plume.styles.copy.S.regular>
                    </plume.Tooltip>
                  </>
                )}
              </plume.Tab>

              <plume.Tab active={navigation.is(PATHS.SUPPORTERS)} disabled={!project.is_online}>
                {project.is_online ? (
                  <TabItem as={RouterLink} to={navigation.pathTo(PATHS.SUPPORTERS)}>
                    {t('Contributions')}&nbsp;
                    {!!project.supporters_count && (
                      <Counter>{project.orders_count || project.supporters_count}</Counter>
                    )}
                  </TabItem>
                ) : (
                  <>
                    <TabItem
                      as={plume.Link}
                      onMouseEnter={() => {
                        toggleTooltip('backers')
                      }}
                      onMouseLeave={() => {
                        toggleTooltip(undefined)
                      }}
                      onTouchStart={() => {
                        toggleTooltip('backers')
                      }}
                      disabled
                    >
                      {t('Contributions')}&nbsp;
                    </TabItem>
                    <plume.Tooltip open={tooltipOpen === 'backers'} position="left">
                      <plume.styles.copy.S.regular>
                        {t('This tab will be visible once the project is live')}
                      </plume.styles.copy.S.regular>
                    </plume.Tooltip>
                  </>
                )}
              </plume.Tab>

              <plume.Tab active={navigation.is(PATHS.COMMENTS)} disabled={!project.is_online}>
                {project.is_online ? (
                  <TabItem as={RouterLink} to={navigation.pathTo(PATHS.COMMENTS)}>
                    {t('Comments')}&nbsp;
                    {!!project.comments_count && <Counter>{project.comments_count}</Counter>}
                  </TabItem>
                ) : (
                  <>
                    <TabItem
                      as={plume.Link}
                      onMouseEnter={() => {
                        toggleTooltip('comments')
                      }}
                      onMouseLeave={() => {
                        toggleTooltip(undefined)
                      }}
                      onTouchStart={() => {
                        toggleTooltip('comments')
                      }}
                      disabled
                    >
                      {t('Comments')}&nbsp;
                    </TabItem>
                    <plume.Tooltip open={tooltipOpen === 'comments'} position="left">
                      <plume.styles.copy.S.regular>
                        {t('This tab will be visible once the project is live')}
                      </plume.styles.copy.S.regular>
                    </plume.Tooltip>
                  </>
                )}
              </plume.Tab>
            </plume.TabMenu>
            {sticky && model.project.hasCheckout(project) && BackNowCTA(false)}
          </MenuNav>
        </MenuContainer>
      </MenuPositionner>

      {sticky && model.project.hasCheckout(project) && BackNowCTA(true)}
    </>
  )

  function BackNowCTA(showOnMobile: boolean) {
    return (
      <CTAWrapper $isProjectTab={isProjectTab} $showOnMobile={showOnMobile}>
        <plume.styles.button.CallToAction onClick={handleBacking} size="medium">
          {projectIsDonationBased ? t('Donate') : t('Back now')}
        </plume.styles.button.CallToAction>
      </CTAWrapper>
    )
  }

  function handleBacking(): void {
    if (project.is_online && !model.project.isFinished<projectModel.ExtraFields>(project)) {
      if (!model.project.isOrdersEnabled<projectModel.ExtraFields>(project)) {
        dispatch(modalAction.toggleModalPaymentDisturbance(true))
      } else {
        tracking.track({
          event: EVENT_TYPE.GENERIC,
          eventCategory: CATEGORY.PROJECT_PAGE,
          eventAction: TRACKING_ACTION.ACCESS_CHECKOUT,
          eventLabel: window.matchMedia(plume.BREAKPOINTS.LAPTOP).matches
            ? TRACKING_SOURCE.STICKY_MENU
            : TRACKING_SOURCE.STICKY_MOBILE,
          eventCallback: () => {
            document.location.href = URLs.getCheckoutURL(project.id)
          },
        })
      }
    } else if (project.post_campaign_link) {
      dispatch(modalAction.toggleModalPostCampaign(true))
    }
  }
}

export const Menu = styled(MenuComponent)``

const MenuPositionner = styled.div`
  background-color: ${plume.COLORS.PRIMARY_GREY_000};
  border-bottom: 1px solid ${plume.COLORS.PRIMARY_SAND_200};
  display: flex;
  justify-content: center;
  width: 100%;
  overflow: auto;
`

const MenuContainer = styled.div`
  display: flex;
  height: 64px;
  justify-content: center;
  position: relative;
  width: 100%;
`

const MenuNav = styled.nav`
  align-items: center;
  display: flex;
  height: 64px;
  justify-content: space-between;
  margin: 0 auto;
  max-width: 1200px;
  overflow-x: auto;
  overflow-y: hidden;
  width: 100%;
  z-index: 2;
  position: absolute;

  ${plume.TabMenu} {
    flex: 0 1 auto;
  }
`

const TabItem = styled(plume.styles.heading.XXXS)`
  align-items: center;
  display: flex;
  height: 64px;
  line-height: 64px;
  outline: none;
  text-decoration: none;
  white-space: pre;

  ${plume.styles.heading.XXXXXS} {
    margin: 5px 0 0 3px;
  }
` as YakComponentOf<PolymorphicComponent<'div', plume.styles.heading.HeadingProps>>

const Counter = styled.div`
  color: ${plume.COLORS.PRIMARY_GREY_500};
`

const CTAWrapper = styled.div<{ $isProjectTab: boolean; $showOnMobile: boolean }>`
  ${plume.styles.button.CallToAction} {
    display: inline-flex;
    margin: 0 20px;
    width: auto;
  }

  /* hide CTA on mobile if user isn't in project detail tab */
  ${({ $showOnMobile }) => {
    if ($showOnMobile) {
      return css`
        display: none;
      `
    }
  }}

  @media not screen and ${plume.BREAKPOINTS.LAPTOP} {
    display: ${({ $isProjectTab, $showOnMobile }) => ($isProjectTab || $showOnMobile ? 'block' : 'none')};
    background-color: ${plume.COLORS.PRIMARY_GREY_000};
    bottom: 0px;
    box-shadow: ${plume.EFFECTS.SHADOW_5};
    box-sizing: border-box;
    left: 0px;
    padding: 10px;
    position: fixed;
    width: 100%;
    z-index: ${plume.ZINDEX.ABOVE};

    ${plume.styles.button.CallToAction} {
      display: flex;
      margin: auto;
      width: 100%;
    }
  }
`
